import { supabase } from '../lib/supabase.js';

const POLL_MS = 30_000;
const MAX_EVENTS = 100;

let initialized = false;
let checking = false;
let visibleEvents = [];
let pollTimer = null;
let dialog = null;
let list = null;
let summary = null;
let acknowledgeButton = null;
let statusLine = null;

const CALCULATOR_NAMES = {
  'etoll-pl':'e-TOLL Polska',
  'a2-awsa-pl':'A2 Autostrada Wielkopolska',
  'a4-sam-pl':'A4 Katowice–Kraków',
  'myto-cz':'MYTO CZ',
  'emyto-sk':'eMyto Słowacja',
  'darsgo-si':'DarsGo Słowenia',
  'asfinag-at':'ASFINAG Austria',
  'hugo-hu':'HU-GO Węgry',
  'hac-hr':'HAC Chorwacja',
  'azm-hr':'Zagreb–Macelj',
  'bina-istra-hr':'Bina-Istra',
  'autostrade-it':'Autostrade Italia',
  'autoroutes-fr':'Autoroutes Francja',
};

export function initAutomationNotifications() {
  if (initialized || !supabase) return;
  initialized = true;

  ensureDialog();
  schedulePoll(1200);

  window.addEventListener('focus', () => schedulePoll(150));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedulePoll(150);
  });

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT' || !session?.access_token) {
      hideDialog(false);
      return;
    }
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      schedulePoll(300);
    }
  });

  pollTimer = window.setInterval(() => {
    checkUnreadAutomationUpdates().catch(logError);
  }, POLL_MS);
}

function schedulePoll(delay = 0) {
  window.setTimeout(() => {
    checkUnreadAutomationUpdates().catch(logError);
  }, Math.max(0, Number(delay) || 0));
}

async function checkUnreadAutomationUpdates() {
  if (checking || isDialogOpen()) return;
  checking = true;

  try {
    const { data:{ session }, error:sessionError } = await supabase.auth.getSession();
    if (sessionError || !session?.access_token) return;

    const { data, error } = await supabase.rpc('d24_list_unread_automation_updates', {
      p_limit:MAX_EVENTS,
    });
    if (error) throw error;

    const rows = Array.isArray(data) ? data : [];
    if (!rows.length) return;
    showDialog(rows);
  } finally {
    checking = false;
  }
}

function ensureDialog() {
  if (dialog) return;

  dialog = document.createElement('div');
  dialog.id = 'automationUpdateDialog';
  dialog.className = 'modal-backdrop automation-update-backdrop hidden';
  dialog.setAttribute('aria-hidden', 'true');

  const card = document.createElement('section');
  card.className = 'modal-card automation-update-modal';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-labelledby', 'automationUpdateTitle');

  const header = document.createElement('div');
  header.className = 'modal-header automation-update-header';

  const titleWrap = document.createElement('div');
  const title = document.createElement('h2');
  title.id = 'automationUpdateTitle';
  title.textContent = 'Automatyczna aktualizacja opłat';
  summary = document.createElement('p');
  summary.className = 'automation-update-summary';
  titleWrap.append(title, summary);

  const closeButton = document.createElement('button');
  closeButton.className = 'icon-button';
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Zamknij');
  closeButton.textContent = '×';
  closeButton.addEventListener('click', acknowledgeAndClose);

  header.append(titleWrap, closeButton);

  list = document.createElement('div');
  list.className = 'automation-update-list';

  statusLine = document.createElement('div');
  statusLine.className = 'status neutral automation-update-status hidden';

  const actions = document.createElement('div');
  actions.className = 'modal-actions automation-update-actions';
  acknowledgeButton = document.createElement('button');
  acknowledgeButton.className = 'button primary';
  acknowledgeButton.type = 'button';
  acknowledgeButton.textContent = 'Rozumiem';
  acknowledgeButton.addEventListener('click', acknowledgeAndClose);
  actions.append(acknowledgeButton);

  card.append(header, list, statusLine, actions);
  dialog.append(card);
  document.body.append(dialog);
}

function showDialog(events) {
  ensureDialog();
  visibleEvents = events.slice(0, MAX_EVENTS);

  const failed = visibleEvents.filter(item => Boolean(item.requires_manual)).length;
  summary.textContent = failed
    ? `${visibleEvents.length} nowych aktualizacji · ${failed} wymaga kontroli ręcznej.`
    : `${visibleEvents.length} nowych aktualizacji · wszystkie zakończone poprawnie.`;

  list.textContent = '';
  for (const event of visibleEvents) list.append(createEventRow(event));

  setStatus('', 'neutral', true);
  dialog.classList.remove('hidden');
  dialog.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  window.setTimeout(() => acknowledgeButton?.focus(), 0);
}

function createEventRow(event) {
  const row = document.createElement('article');
  row.className = `automation-update-row ${event.requires_manual ? 'needs-manual' : 'is-ok'}`;

  const top = document.createElement('div');
  top.className = 'automation-update-row-top';

  const title = document.createElement('div');
  title.className = 'automation-update-row-title';
  const lineName = String(event.route_number || 'Nieznana linia');
  const calculator = CALCULATOR_NAMES[event.calculator_key] || event.calculator_key || 'Kalkulator';
  title.textContent = `${lineName} · ${calculator}`;

  const badge = document.createElement('span');
  badge.className = `automation-update-result-badge ${event.requires_manual ? 'error' : 'ok'}`;
  badge.textContent = event.requires_manual ? 'WYMAGA KONTROLI' : 'OK';
  top.append(title, badge);

  const meta = document.createElement('div');
  meta.className = 'automation-update-meta';
  meta.append(
    metaItem('Źródło', sourceLabel(event)),
    metaItem('Cena', event.price_ok ? `✓ ${event.cost_text || formatCost(event)}` : '✗ brak poprawnej ceny', event.price_ok),
    metaItem('Screenshot', event.screenshot_ok ? '✓ zapisany' : '✗ brak / błąd', event.screenshot_ok),
    metaItem('Data', formatDateTime(event.calculated_at)),
  );

  row.append(top, meta);

  const message = String(event.error_message || '').trim();
  if (event.requires_manual || message) {
    const note = document.createElement('div');
    note.className = 'automation-update-error';
    note.textContent = message || 'Automatyczna aktualizacja nie jest kompletna. Uruchom ten kalkulator ręcznie i sprawdź wynik.';
    row.append(note);
  }

  return row;
}

function metaItem(label, value, ok = null) {
  const item = document.createElement('div');
  item.className = 'automation-update-meta-item';
  if (ok === true) item.classList.add('ok');
  if (ok === false) item.classList.add('error');

  const name = document.createElement('span');
  name.textContent = label;
  const text = document.createElement('strong');
  text.textContent = String(value || '—');
  item.append(name, text);
  return item;
}

async function acknowledgeAndClose() {
  if (!visibleEvents.length || !acknowledgeButton) {
    hideDialog(false);
    return;
  }

  acknowledgeButton.disabled = true;
  const previous = acknowledgeButton.textContent;
  acknowledgeButton.textContent = 'Zapisuję…';
  setStatus('', 'neutral', true);

  try {
    const ids = visibleEvents.map(item => item.event_id).filter(Boolean);
    const { error } = await supabase.rpc('d24_mark_automation_updates_read', {
      p_event_ids:ids,
    });
    if (error) throw error;

    hideDialog(true);
    schedulePoll(250);
  } catch (error) {
    console.error('[D24 AUTOMATY] Nie udało się zapisać potwierdzenia:', error);
    setStatus(`Nie udało się zapisać potwierdzenia: ${error.message}`, 'error', false);
  } finally {
    acknowledgeButton.disabled = false;
    acknowledgeButton.textContent = previous;
  }
}

function hideDialog(clearEvents = true) {
  if (!dialog) return;
  dialog.classList.add('hidden');
  dialog.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
  if (clearEvents) visibleEvents = [];
}

function isDialogOpen() {
  return Boolean(dialog && !dialog.classList.contains('hidden'));
}

function setStatus(message, type = 'neutral', hidden = false) {
  if (!statusLine) return;
  statusLine.textContent = message || '';
  statusLine.className = `status ${type} automation-update-status${hidden ? ' hidden' : ''}`;
}

function sourceLabel(event) {
  if (event.source === 'oracle') return 'Oracle · automat tygodniowy';
  if (event.source === 'browserless') return 'Chmura Browserless · automat miesięczny';
  return String(event.source || 'Automat');
}

function formatCost(event) {
  const value = Number(event.cost_value);
  if (!Number.isFinite(value)) return '—';
  return `${value.toLocaleString('pl-PL', { maximumFractionDigits:2 })} ${event.currency || ''}`.trim();
}

function formatDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle:'short',
    timeStyle:'short',
  }).format(date);
}

function logError(error) {
  console.warn('[D24 AUTOMATY] Powiadomienia:', error?.message || error);
}
